'use client';

import { PropsWithChildren, ReactNode, useState, memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo, Node } from '@headless-workspace/typings';
import { VariantProps } from 'class-variance-authority';
import {
    GlowIcon,
    GlowIconProps,
    GlowTextBodySmall,
    GlowTextCaption,
    GlowTypography,
    GlowTypographyProps,
    typographyStyle,
} from '../atoms';
import { GlowButton, GlowClickable } from '../molecules';
import { buttonStyle } from '../molecules/cta/buttons/buttonStyle';
import { PropsWithClassName, PropsWithIcon, PropsWithTagName } from '../props';
import { Icons } from '../res/icons';
import { extractContent } from '../utils';

export type CTA = {
    label: string;
    href: string;
};

type CardTextProps = Pick<GlowTypographyProps, 'fontSize' | 'fontWeight' | 'TagName' | 'className'> & {
    content: ReactNode;
    fontFamily?: VariantProps<typeof typographyStyle>['fontFamily'];
};

/**
 * Component that renders a title and subtitle within a banner card.
 *
 * @param {CardTitleProps} props - The properties for the CardHeader component.
 * @param {React.ReactNode} props.title - The element containing the title content.
 * @param {React.ReactNode} props.subTitle - The element containing the subtitle content.
 * @param {string} [props.fontSize] - The font size for the title.
 * @param {string} [props.fontWeight] - The font weight for the title.
 * @param {React.ElementType} [props.TagName] - The HTML tag to use for the title.
 * @param {string} [props.className] - Additional CSS classes to apply to the container.
 *
 */
const Text: ComponentMemo<CardTextProps> = memo(({ content, fontSize, fontWeight, TagName, className, fontFamily }) => {
    const textElement = extractContent(content);
    if (!textElement) {
        return null;
    }
    return (
        <GlowTypography
            className={className}
            innerHTML={content}
            fontSize={fontSize}
            fontWeight={fontWeight}
            TagName={TagName}
            fontFamily={fontFamily}
        />
    );
});

type CardPromoProps = GlowTypographyProps & {
    label?: ReactNode;
    content?: Node;
    fontFamily?: VariantProps<typeof typographyStyle>['fontFamily'];
};

/**
 * A functional component that renders a banner card content.
 *
 * @component
 * @param {CardContentProps} props - The properties for the Card content.
 * @param {React.ReactNode} props.labelElement - The label element to be displayed.
 * @param {React.ReactNode} props.contentElement - The content element to be displayed.
 */
const Promo: ComponentMemo<CardPromoProps> = memo(
    ({ label, content, fontSize = 'body', fontWeight, TagName = 'div', className, fontFamily }: CardPromoProps) => {
        return (
            <div className={'flex flex-row gap-0.75 items-center'}>
                {extractContent(label) && (
                    <div
                        className={clsx(
                            'flex flex-col justify-center items-center text-center',
                            'h-homeBannerLabel w-homeBannerLabel',
                            'bg-background-neutral rounded-0.25',
                            'px-0.75',
                            'line-clamp-1',
                            'font-brand font-bold text-body',
                            'text-text-primary',
                        )}
                    >
                        {label}
                    </div>
                )}
                <GlowTypography
                    className={clsx('flex-1 line-clamp-2', className)}
                    innerHTML={content}
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    TagName={TagName}
                    fontFamily={fontFamily}
                />
            </div>
        );
    },
);

export type CardButtonProps = VariantProps<typeof buttonStyle> &
    PropsWithClassName & {
        cta?: CTA;
        fontFamily?: VariantProps<typeof typographyStyle>['fontFamily'];
    };

/**
 * Button component that renders a `GlowButton` if the `button` prop is provided.
 *
 * @param {CardButtonProps} props - The properties for the Button component.
 * @param {object} props.button - The button object containing label and href.
 * @param {string} props.button.label - The label for the button.
 * @param {string} props.button.href - The URL the button links to.
 * @param {string} [props.variant='secondary'] - The variant of the button, defaults to 'secondary'.
 */
const Button: ComponentMemo<CardButtonProps> = memo(
    ({ cta, variant = 'secondary', size = 'large', width = 'auto', className, fontFamily }: CardButtonProps) => {
        return (
            cta && (
                <GlowButton
                    fontFamily={fontFamily}
                    className={clsx('z-1', className)}
                    {...cta}
                    variant={variant}
                    size={size}
                    width={width}
                />
            )
        );
    },
);

type CardLegalMentionProps = PropsWithChildren & PropsWithClassName & PropsWithTagName;

/**
 * A component that conditionally renders its children based on their content.
 * If the children have content, it renders them inside a span with a specified class name.
 * If the children are empty, it renders an empty span with a different class name.
 *
 * @param {CardMentionLabelProps} props - The properties for the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the span.
 * @param {Object} [props.classNames] - An optional object containing class names.
 * @param {string} [props.className] - The class name to apply when children have content.
 */
const LegalMention: ComponentMemo<CardLegalMentionProps> = memo(
    ({ children, className, TagName = 'span' }: CardLegalMentionProps) => {
        return (
            extractContent(children) && (
                <GlowTextCaption TagName={TagName} className={clsx('line-clamp-2', className)} text={children} />
            )
        );
    },
);

type CardIconProps = PropsWithClassName &
    PropsWithIcon & {
        type?: GlowIconProps['type'];
        iconClassName?: string;
    };

/**
 * A functional component that renders an icon within a styled container.
 *
 * @param {CardIconProps} props - The properties for the Icon component.
 * @param {string} props.className - Additional CSS classes to apply to the container.
 * @param {React.ElementType} props.Icon - The icon component to render.
 *
 * @returns {JSX.Element} A JSX element containing the styled icon.
 */
const Icon: ComponentMemo<CardIconProps> = memo(({ className, Icon, type = 'large', iconClassName }: CardIconProps) => {
    return (
        <div className={clsx('flex items-center', className)}>
            <GlowIcon Icon={Icon} className={iconClassName} type={type} />
        </div>
    );
});

type CardTagsProps = {
    tags?: CTA[];
};

/**
 * Component that renders a list of tags as buttons.
 *
 * @component
 * @param {CardTagsProps} props - The properties for the Tags component.
 * @param {Array<{ label: string, href: string }>} props.tags - An array of tag objects, each containing a label and href.
 */
const Tags: ComponentMemo<CardTagsProps> = memo(({ tags }: CardTagsProps) => {
    if (!tags) {
        return null;
    }

    return (
        <div
            className={clsx(
                'flex flex-row tablet:flex-wrap',
                'max-w-mainBannerFastAccessLinks tablet:max-w-mainBannerFastAccessLinksTablet desktopS:max-w-none',
                '-ml-1 tablet:ml-0',
                'gap-0.5',
                'scrollbar-hide overflow-x-scroll',
            )}
        >
            {tags.map(({ label, href }, index) => (
                <GlowButton
                    key={`${label}-${index}`}
                    className={'first:ml-1 last:mr-1 tablet:first:ml-0 tablet:last:mr-0'}
                    label={label}
                    href={href}
                    variant={'onElement'}
                    size={'medium'}
                    fontSize={'bodySmall'}
                />
            ))}
        </div>
    );
});

export type CardAccordionProps = {
    accordion?: {
        title: ReactNode;
        content: ReactNode;
    };
};

/**
 * Accordion component that displays a clickable header and expandable content.
 *
 * @component
 * @param {CardAccordionProps} props - The properties for the Accordion component.
 * @param {Object} props.accordion - The accordion data.
 * @param {string} props.accordion.title - The title of the accordion.
 * @param {string} props.accordion.description - The description of the accordion.
 *
 * @example
 * <Accordion accordion={{ title: 'Accordion Title', description: 'Accordion Description' }} />
 */
const Accordion: ComponentMemo<CardAccordionProps> = memo(({ accordion }: CardAccordionProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const toggleExpand = () => {
        setIsExpanded((prevState) => !prevState);
    };

    if (!accordion) {
        return null;
    }

    return (
        <div className={'flex flex-col mt-0.25 gap-0.5'}>
            <GlowClickable
                onClick={toggleExpand}
                content={{
                    labelElement: (
                        <>
                            <GlowTextBodySmall
                                text={accordion.title}
                                className={'z-1 text-wrap text-left text-underline'}
                                TagName={'span'}
                            />
                            <GlowIcon
                                Icon={Icons.ChevronDown}
                                type={'medium'}
                                className={clsx('z-1 duration-moderate1 ease-linear', isExpanded && 'rotate-180')}
                            />
                        </>
                    ),
                }}
            />
            {isExpanded && (
                <GlowTextCaption TagName={'div'} text={accordion.content} className={'text-wrap'} color={'tertiary'} />
            )}
        </div>
    );
});

export const ComposableContent = {
    Text,
    Button,
    LegalMention,
    Tags,
    Accordion,
    Icon,
    Promo,
};

ComposableContent.Text.displayName = 'ComposableContentText';
ComposableContent.Button.displayName = 'ComposableContentButton';
ComposableContent.LegalMention.displayName = 'ComposableContentLegalMention';
ComposableContent.Tags.displayName = 'ComposableContentTags';
ComposableContent.Accordion.displayName = 'ComposableContentAccordion';
ComposableContent.Icon.displayName = 'ComposableContentIcon';
ComposableContent.Promo.displayName = 'ComposableContentPromo';
